import React from "react";
import ApiLink from "../../ui/ApiLink";
import JsxSyntaxHighlighter from "../../ui/JsxSyntaxHighlighter";

export default function TypescriptAndCasting() {
  return (
    <>
      <p>
        You're going to want proper typings for the resources that you fetch.
        Since these types are not known at compile time, we have to cast the
        response to the expected type for each call. Because the ProFile json
        api types will change over time, those types are not included in
        minibeak itself. However, minibeak contains generics and inheritable
        interfaces that help with the casting.
      </p>
      <p>
        Start by implementing resource types that inherit from{" "}
        <code>ProFileResource</code>, for example an{" "}
        <code>AchievementResource</code> that extends{" "}
        <code>ProFileResource</code>. Now you can use the generic versions of{" "}
        <ApiLink name="useProFile" /> and{" "}
        <ApiLink name="processProFileResult" />:
      </p>

      <JsxSyntaxHighlighter>{`interface AchievementResource extends ProFileResource {
  name: string;
  // ...
}

// Single resource
useProFile<AchievementResource>({...});
// Collection
useProFile<AchievementResource[]>({...});

// Same for processProFileResult:
processProFileResult<AchievementResource>(response);
// Or for a collection:
processProFileResult<AchievementResource[]>(response);

`}</JsxSyntaxHighlighter>

      <p>
        The <code>ProFileResource</code> interface will let you read fields
        without an explicit typing as the <code>any</code> type. If you want
        stricter typechecking on resources, you can inherit from{" "}
        <code>StrictProFileResource</code> instead. This will only let you read
        fields that are defined in the type.
      </p>

      <h4>Typing the resource lookup</h4>

      <p>
        You can also implement a <code>ProFileResourceLookup</code> type for the
        returned resource lookup (the field called <code>resources</code> on{" "}
        <ApiLink name="MinibeakResult" />
        ). This type should be the same for all backend calls. For example:
      </p>

      <JsxSyntaxHighlighter>{`interface ProFileResourceLookup extends MinibeakResourceLookup {
  achievementTypes?: Record<string, AchievementTypeResource>;
  achievements?: Record<string, AchievementResource>;
  // and all of the other types...
}

useProFile<AchievementResource, ProFileResourceLookup>({...})
useProFile<AchievementResource[], ProFileResourceLookup>({...})
processProFileResult<AchievementResource, ProFileResourceLookup>(response)
processProFileResult<AchievementResource[], ProFileResourceLookup>(response)`}</JsxSyntaxHighlighter>

      <h4>Typing callProFile</h4>
      <p>
        Raw responses from callProFile can be cast to anything, but you can use{" "}
        <code>RawProFileResource</code> or <code>RawProFileResource[]</code>{" "}
        together with <code>ProFileRawResponse</code> to get the basic fields
        like id, type, etc:
      </p>
      <JsxSyntaxHighlighter>{`callProFile<ProFileRawResponse<RawProFileResource>>({...});
// Or for a collection:
callProFile<ProFileRawResponse<RawProFileResource[]>>({...});`}</JsxSyntaxHighlighter>
      <p>
        Note that <code>RawProFileResource</code> is different from{" "}
        <code>ProFileResource</code>. The first is returned from{" "}
        <ApiLink name="callProFile" /> (it is the raw json from backend). The
        second is a processed resource returned from{" "}
        <ApiLink name="useProFile" /> or <ApiLink name="processProFileResult" />
        . All of your normal resource type definitions for{" "}
        <ApiLink name="useProFile" /> and{" "}
        <ApiLink name="processProFileResult" /> should inherit from{" "}
        <code>ProFileResource</code>.
      </p>
    </>
  );
}
